// src/ryan/components/product-blocks/MetodoDemoBlock.tsx
// Bloco de demonstração "Método GRAIA" — não é "mais uma funcionalidade", é o
// bloco que explica a FILOSOFIA da plataforma: apostila e flashcard não são
// duas ferramentas, são o mesmo conteúdo em dois momentos do estudo. Os
// outros três blocos (OSCE, Flashcards, Apostilas) mostram O QUÊ a
// plataforma faz; este mostra COMO tudo se conecta — por isso a coreografia
// central (Fase 2) é o único Shared Element Transition deste bloco, reservado
// justamente pro momento em que essa conexão vira literal na tela.
//
// Diferente dos outros dois blocos de demo: NÃO usa o layout dividido
// (rpb-demo-grid/col-demo/col-pillars) nem um shell fechado tipo "app
// simulado" — aqui a metáfora é espaço aberto onde uma conexão acontece, não
// uma janela de produto. Ver rpb-met-canvas (full-width) e DemoPillarsList
// com layout="row" (grade horizontal abaixo do canvas, não coluna lateral).

import React, { useState, useEffect, useRef, useId } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowRight, MessageSquare } from 'lucide-react';
import {
    DemoPillar, DemoClosing, DemoHeader, DemoPillarsList, DemoClosingPanel,
    resolvePillarIcon,
} from './DemoBlockShared';
import './MetodoDemoBlock.css';

// ── Tipos ──────────────────────────────────────────────────────────────

export interface MetodoDemoWorld {
    icon: string;
    title: string;
    subtitle: string;
}

export interface MetodoDemoData {
    label: string;
    headline: string;
    subheadline: string;
    leftWorld: MetodoDemoWorld;
    rightWorld: MetodoDemoWorld;
    methodName: string;
    methodSteps: string[];
    demoParagraph: string;
    demoHighlightText: string;
    demoNoteText: string;
    demoFlashcardFront: string;
    demoFlashcardBack: string;
    demoMasteryBefore: number;
    demoMasteryAfter: number;
    closingHeadline: string;
    pillars: DemoPillar[];
    closing: DemoClosing;
}

export const defaultMetodoDemoData = (): MetodoDemoData => ({
    label: 'O Método',
    headline: 'Não são duas ferramentas. É o mesmo conteúdo, em dois momentos do seu estudo.',
    subheadline: 'O que você grifa e anota na apostila vira, automaticamente, o flashcard que vai te fazer lembrar depois. Veja como.',
    leftWorld: { icon: 'ScrollText', title: 'Apostilas', subtitle: 'Leitura que gera conhecimento ativo' },
    rightWorld: { icon: 'Zap', title: 'Flashcards', subtitle: 'Revisão que se adapta a você' },
    methodName: 'Método GRAIA',
    methodSteps: ['Aprenda.', 'Entenda.', 'Revise.', 'Lembre.'],
    demoParagraph: 'A hipertensão arterial sistêmica é definida por níveis pressóricos persistentemente elevados, sendo um dos principais fatores de risco modificáveis para doença cardiovascular.',
    demoHighlightText: 'níveis pressóricos persistentemente elevados',
    demoNoteText: 'Rever os critérios diagnósticos da HAS antes da prova.',
    // Front igual ao trecho grifado de propósito — é o que faz a travessia
    // (Fase 2) pousar sem nenhuma troca de texto perceptível.
    demoFlashcardFront: 'níveis pressóricos persistentemente elevados',
    demoFlashcardBack: 'Definição de hipertensão arterial sistêmica — um dos principais fatores de risco modificáveis para doença cardiovascular.',
    demoMasteryBefore: 20,
    demoMasteryAfter: 55,
    closingHeadline: 'Um único fluxo de estudo. Da primeira leitura até a revisão final.',
    pillars: [
        { id: 'pil-1', icon: 'Layers', title: 'Nada se perde entre as etapas', text: 'O que você grifa na apostila vira material de revisão automaticamente — sem precisar copiar nada pra outro lugar.' },
        { id: 'pil-2', icon: 'Zap', title: 'Do entendimento à memorização', text: 'Primeiro você entende o conceito lendo. Depois, o Ryan garante que ele não seja esquecido.' },
        { id: 'pil-3', icon: 'ArrowRight', title: 'Um fluxo, não duas ferramentas', text: 'Apostila e flashcards conversam entre si — você estuda uma vez, revisa pelo tempo que precisar.' },
    ],
    closing: {
        lead: 'Não são dois produtos.',
        emphasis: 'É um único método, pensado do início ao fim.',
        items: [
            'Você lê, grifa e anota — e isso já é o começo da sua revisão.',
            'O sistema transforma sua leitura em prática de recuperação ativa.',
            'Nenhuma etapa fica solta: leitura, entendimento e memória caminham juntos.',
        ],
    },
});

const WorldCard: React.FC<{ world: MetodoDemoWorld }> = ({ world }) => {
    const Icon = resolvePillarIcon(world.icon);
    return (
        <div className="rpb-met-world">
            <div className="rpb-met-world-icon"><Icon size={30} strokeWidth={1.6} /></div>
            <p className="rpb-met-world-title">{world.title}</p>
            <p className="rpb-met-world-subtitle">{world.subtitle}</p>
        </div>
    );
};

// ── Coreografia interna (fases) ─────────────────────────────────────────
// Mesmo mecanismo já usado em ApostilaDemoInner: useEffect com timers em
// cascata, acionado por `active`, resetando tudo quando active vira false —
// aqui com um acréscimo: ao fim da cascata, incrementa `cycleKey` (também
// nas deps do efeito) pra reiniciar o ciclo inteiro, criando o loop.
//
// DIREÇÃO DE MOTION (reescrita — mesma arquitetura, timing/ênfase novos):
// em cada momento existe só UM protagonista visual; tudo o resto vira
// contexto (dimmed, parado, neutro). O ciclo é mais longo e pausado de
// propósito — poucas coisas em movimento ao mesmo tempo, muitas pausas,
// controle total de pra onde o olho vai. Ciclo completo dura ~11s.

const MetodoDemoInner: React.FC<{ data: MetodoDemoData; active: boolean }> = ({ data, active }) => {
    const [worldsUnited, setWorldsUnited] = useState(false);
    // Dimming de hierarquia — sempre que existe um protagonista (grifo,
    // depois card), os mundos perdem destaque (opacity), sem sair de cena.
    const [dimWorlds, setDimWorlds] = useState(false);
    const [paragraphHighlighted, setParagraphHighlighted] = useState(false);
    const [blankCardVisible, setBlankCardVisible] = useState(false);
    // Fase 2a — o card "atrai" o texto antes da travessia começar de fato
    // (glow crescendo + pausa breve) — cria causa e efeito em vez do texto
    // simplesmente sair viajando do nada.
    const [cardAttracting, setCardAttracting] = useState(false);
    // Fase 2b — só existe um elemento com layoutId de cada vez: o grifo (na
    // apostila) OU o card em trânsito. `textTraveling` controla essa troca.
    const [textTraveling, setTextTraveling] = useState(false);
    // Assim que a travessia termina, o elemento com layoutId é substituído
    // por uma face de card comum (sem motion/layoutId) — layoutId fica
    // escopado só ao instante da travessia em si, nada mais depois disso.
    const [cardComplete, setCardComplete] = useState(false);
    // Pulso de "chegou" — só nos ~400ms logo após pousar, nunca reaplicado
    // nos flips seguintes (que usam a classe --flipping, à parte).
    const [justLanded, setJustLanded] = useState(false);
    // Card fisicamente "virando" (ida OU volta) — controla só a keyframe do
    // giro; o conteúdo em si troca no meio do giro (ver pushFlip), não
    // junto com o início/fim da animação.
    const [flipping, setFlipping] = useState(false);
    const [cardFlipped, setCardFlipped] = useState(false);
    const [dimCard, setDimCard] = useState(false);
    const [masteryRaised, setMasteryRaised] = useState(false);
    const [masteryPulse, setMasteryPulse] = useState(false);
    // Qual passo (Aprenda/Entenda/Revise/Lembre) está com destaque agora —
    // acompanha a narrativa por cor/peso, nunca por movimento.
    const [activeStepIndex, setActiveStepIndex] = useState<number | null>(null);
    const [resetting, setResetting] = useState(false);
    const [cycleKey, setCycleKey] = useState(0);

    // layoutId — único por instância montada via useId(), com prefixo
    // próprio (met-highlight-) que nunca colide com o apt-quiz-${uid} de
    // ApostilaDemoBlock, mesmo que os dois blocos existam juntos na mesma
    // página pública (dois ContentBlocks diferentes) — cada useId() já é
    // garantidamente único por instância React montada.
    const uid = useId();
    const highlightLayoutId = `met-highlight-${uid}`;

    const shouldReduceMotion = useReducedMotion();

    const resetPhases = () => {
        setWorldsUnited(false);
        setDimWorlds(false);
        setParagraphHighlighted(false);
        setBlankCardVisible(false);
        setCardAttracting(false);
        setTextTraveling(false);
        setCardComplete(false);
        setJustLanded(false);
        setFlipping(false);
        setCardFlipped(false);
        setDimCard(false);
        setMasteryRaised(false);
        setMasteryPulse(false);
        setActiveStepIndex(null);
        setResetting(false);
    };

    useEffect(() => {
        if (!active) { resetPhases(); return; }

        // prefers-reduced-motion: pula direto pro estado final, sem cascata
        // de fases, sem layoutId, sem dimming/destaque de passo (são
        // linguagem de MOVIMENTO, não fazem sentido isolados) — e sem loop.
        if (shouldReduceMotion) {
            setWorldsUnited(true);
            setParagraphHighlighted(true);
            setBlankCardVisible(true);
            setTextTraveling(true);
            setCardComplete(true);
            setCardFlipped(false);
            setMasteryRaised(true);
            return;
        }

        resetPhases();
        const timers: ReturnType<typeof setTimeout>[] = [];
        const FLIP_MS = 700;
        // O giro (scaleX) e a troca de conteúdo são duas coisas separadas
        // de propósito: o conteúdo troca exatamente no meio do giro (quando
        // o card está "de perfil", quase invisível), nunca no início/fim —
        // é isso que faz o flip parecer um cartão físico virando, não um
        // corte seco de conteúdo.
        const pushFlip = (toBack: boolean, at: number) => {
            timers.push(setTimeout(() => setFlipping(true), at));
            timers.push(setTimeout(() => setCardFlipped(toBack), at + FLIP_MS / 2));
            timers.push(setTimeout(() => setFlipping(false), at + FLIP_MS));
        };

        let cursor = 400;

        // ── FASE 0 — Os mundos entram e se unem. Depois, silêncio total
        // (~700ms) — nada mais acontece, só o micro-floating ambiente
        // (CSS puro, ver .rpb-met-worlds--united .rpb-met-world). ──
        timers.push(setTimeout(() => setWorldsUnited(true), cursor));
        cursor += 1300; // ~0.6s de aproximação + ~700ms de silêncio puro

        // ── FASE 1 — O grifo é o ÚNICO protagonista. Card, barra e passos
        // seguem parados; os mundos perdem destaque (dim), sem sumir. ──
        timers.push(setTimeout(() => {
            setParagraphHighlighted(true);
            setBlankCardVisible(true);
            setDimWorlds(true);
            setActiveStepIndex(0); // "Aprenda."
        }, cursor));
        cursor += 1500; // pausa perceptível — só olhar pro grifo, nada mais se move

        // ── FASE 2 — Antes da travessia, o card "atrai" o texto: glow
        // crescendo (~300ms) + pequena pausa com o glow no máximo (~200ms).
        // Só DEPOIS disso o Shared Element Transition começa. ──
        timers.push(setTimeout(() => {
            setCardAttracting(true);
            setActiveStepIndex(1); // "Entenda."
        }, cursor));
        cursor += 500;

        // Travessia — lenta, suave, sem pressa: "um pensamento migrando",
        // não um elemento voando.
        timers.push(setTimeout(() => setTextTraveling(true), cursor));
        cursor += 1050;
        timers.push(setTimeout(() => {
            setCardComplete(true);
            setJustLanded(true);
        }, cursor));
        timers.push(setTimeout(() => setJustLanded(false), cursor + 420));
        cursor += 550; // pouso — pausa antes do flip (o cérebro registra "virou um flashcard")

        // ── FLIP — mecânico, preciso, sem bounce. Revela a resposta. ──
        pushFlip(true, cursor);
        timers.push(setTimeout(() => setActiveStepIndex(2), cursor)); // "Revise."
        cursor += FLIP_MS + 300; // giro + pausa de causalidade antes do domínio

        // ── DOMÍNIO — consequência direta do flip, não um timer solto. ──
        timers.push(setTimeout(() => {
            setMasteryRaised(true);
            setMasteryPulse(true);
            setDimCard(true);
            setActiveStepIndex(3); // "Lembre."
        }, cursor));
        cursor += 650;
        timers.push(setTimeout(() => {
            setMasteryPulse(false);
            setDimCard(false);
        }, cursor));
        cursor += 900; // hold — resposta + domínio elevado, lado a lado

        // Assenta de volta pra frente antes da pausa final.
        pushFlip(false, cursor);
        cursor += FLIP_MS;
        cursor += 2000; // pausa final — cena inteira parada antes do reset

        // ── RESET — fade out geral, replay. ──
        timers.push(setTimeout(() => setResetting(true), cursor));
        cursor += 600;
        timers.push(setTimeout(() => setCycleKey(k => k + 1), cursor));

        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, data, cycleKey, shouldReduceMotion]);

    const highlightIdx = data.demoParagraph.indexOf(data.demoHighlightText);
    const paragraphBefore = highlightIdx >= 0 ? data.demoParagraph.slice(0, highlightIdx) : data.demoParagraph;
    const paragraphAfter = highlightIdx >= 0 ? data.demoParagraph.slice(highlightIdx + data.demoHighlightText.length) : '';

    // `layout` separado de `opacity` — a ENTRADA do grifo (fade, Fase 1) é
    // rápida; a TRAVESSIA em si (layout — o que o Framer Motion usa pra
    // resolver o FLIP entre os dois elementos de layoutId) é lenta,
    // ease-in-out, sem overshoot: "um pensamento migrando", não um
    // elemento voando.
    const sharedTransition = {
        layout: { duration: shouldReduceMotion ? 0 : 1.05, ease: [0.45, 0, 0.15, 1] as const },
        opacity: { duration: shouldReduceMotion ? 0 : 0.3 },
    };

    return (
        <>
            <div className={`rpb-met-worlds ${worldsUnited ? 'rpb-met-worlds--united' : ''} ${dimWorlds ? 'rpb-met-worlds--dim' : ''}`}>
                <WorldCard world={data.leftWorld} />
                <div className="rpb-met-worlds-connector">
                    <span className="rpb-met-worlds-plus">+</span>
                    <span className="rpb-met-worlds-method">{data.methodName}</span>
                </div>
                <WorldCard world={data.rightWorld} />
            </div>

            <div className={`rpb-met-dynamic ${resetting ? 'rpb-met-dynamic--resetting' : ''}`}>
                {data.methodSteps.length > 0 && (
                    <div className="rpb-met-steps">
                        {data.methodSteps.map((step, i) => (
                            <React.Fragment key={i}>
                                <div className={`rpb-met-step${activeStepIndex === i ? ' rpb-met-step--active' : ''}`}>
                                    <span className="rpb-met-step-num">{i + 1}</span>
                                    <span className="rpb-met-step-text">{step}</span>
                                </div>
                                {i < data.methodSteps.length - 1 && (
                                    <span className="rpb-met-step-arrow"><ArrowRight size={13} /></span>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                )}

                <div className="rpb-met-preview">
                    <div className="rpb-met-preview-paragraph">
                        <p className="rpb-met-preview-hint">Trecho da apostila</p>
                        <p className="rpb-met-preview-text">
                            {highlightIdx >= 0 ? (
                                <>
                                    {paragraphBefore}
                                    {paragraphHighlighted && !textTraveling && (
                                        <motion.mark
                                            layoutId={highlightLayoutId}
                                            className="rpb-met-highlight"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={sharedTransition}
                                        >
                                            {data.demoHighlightText}
                                        </motion.mark>
                                    )}
                                    {!paragraphHighlighted && !textTraveling && data.demoHighlightText}
                                    {/* Enquanto o trecho real está "em voo" (motion.div lá no
                                        card), mantém aqui só um rastro esmaecido — sem isso, a
                                        frase ficava com um buraco no meio durante a travessia. */}
                                    {textTraveling && (
                                        <span className="rpb-met-highlight-ghost">{data.demoHighlightText}</span>
                                    )}
                                    {paragraphAfter}
                                </>
                            ) : data.demoParagraph}
                        </p>
                        {data.demoNoteText && (
                            <div className="rpb-met-preview-note">
                                <MessageSquare size={11} />
                                <span>{data.demoNoteText}</span>
                            </div>
                        )}
                    </div>

                    <div className="rpb-met-preview-arrow"><ArrowRight size={20} /></div>

                    <div className={`rpb-met-preview-card${dimCard ? ' rpb-met-preview-card--dim' : ''}`}>
                        <div className="rpb-met-card-slot">
                            {/* Não é mais um "placeholder" com "?" — uma superfície quase
                                vazia, respirando bem devagar, que só "acorda" (glow, ver
                                --attracting) no instante exato em que vai atrair o texto. */}
                            {blankCardVisible && !textTraveling && (
                                <div className={`rpb-met-card-blank${cardAttracting ? ' rpb-met-card-blank--attracting' : ''}`} />
                            )}

                            <AnimatePresence>
                                {textTraveling && !cardComplete && (
                                    <motion.div
                                        layoutId={highlightLayoutId}
                                        className="rpb-met-preview-card-face rpb-met-preview-card-front rpb-met-preview-card-face--flying"
                                        transition={sharedTransition}
                                    >
                                        <p className="rpb-met-preview-card-hint">Pergunta</p>
                                        <p className="rpb-met-preview-card-text">{data.demoHighlightText}</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* LayoutId já cumpriu seu papel — daqui pra frente (flip de
                                resposta, reset, replay) é só uma div comum, sem motion, pra
                                nunca competir com o transform que o Framer Motion controla
                                enquanto o elemento acima está "voando". --impact (pulso de
                                chegada) e --flipping (giro mecânico) são classes à parte,
                                cada uma só ativa na sua própria janela de tempo — nunca as
                                duas juntas, pra nunca competirem pela mesma `animation`. */}
                            {cardComplete && (
                                <div
                                    className={[
                                        'rpb-met-preview-card-face',
                                        'rpb-met-preview-card-face--landed',
                                        justLanded ? 'rpb-met-preview-card-face--impact' : '',
                                        flipping ? 'rpb-met-preview-card-face--flipping' : '',
                                        cardFlipped ? 'rpb-met-preview-card-back' : 'rpb-met-preview-card-front',
                                    ].filter(Boolean).join(' ')}
                                >
                                    <p className="rpb-met-preview-card-hint">{cardFlipped ? 'Resposta' : 'Pergunta'}</p>
                                    <p className="rpb-met-preview-card-text">
                                        {cardFlipped ? data.demoFlashcardBack : (data.demoFlashcardFront || data.demoHighlightText)}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="rpb-met-mastery-wrap">
                    <div className="rpb-met-mastery-row">
                        <span className="rpb-met-mastery-label">Domínio</span>
                        <span className={`rpb-met-mastery-value ${masteryPulse ? 'rpb-met-mastery-value--pulse' : ''}`}>
                            {masteryRaised ? data.demoMasteryAfter : data.demoMasteryBefore}%
                        </span>
                    </div>
                    <div className="rpb-met-mastery-track">
                        <div
                            className={`rpb-met-mastery-fill ${masteryPulse ? 'rpb-met-mastery-fill--pulse' : ''}`}
                            style={{ width: `${masteryRaised ? data.demoMasteryAfter : data.demoMasteryBefore}%` }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

// ── Componente público ────────────────────────────────────────────────

const MetodoDemoBlock: React.FC<{ data: MetodoDemoData }> = ({ data }) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12 }
        );
        if (wrapRef.current) observer.observe(wrapRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div className="rpb-demo-wrap" ref={wrapRef}>
            <DemoHeader label={data.label} headline={data.headline} subheadline={data.subheadline} visible={visible} />

            {/* Full-width — sem dividir espaço com os pilares (que agora vêm
                em linha horizontal abaixo, não numa coluna lateral). */}
            <div className={`rpb-met-canvas ${visible ? 'rpb-met-canvas--visible' : ''}`}>
                <MetodoDemoInner data={data} active={visible} />
            </div>

            {data.pillars.length > 0 && (
                <div className="rpb-met-pillars-row">
                    <DemoPillarsList pillars={data.pillars} visible={visible} layout="row" />
                </div>
            )}

            {data.closingHeadline && (
                <p className={`rpb-met-mid-headline ${visible ? 'rpb-met-mid-headline--visible' : ''}`}>
                    {data.closingHeadline}
                </p>
            )}

            <DemoClosingPanel closing={data.closing} visible={visible} />
        </div>
    );
};

export default MetodoDemoBlock;
